import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { z } from 'zod'

import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Card, CardBody } from '@/components/ui/Card'
import { Field } from '@/components/ui/Field'
import { Input } from '@/components/ui/Input'
import { PasswordInput } from '@/components/ui/PasswordInput'
import { useAuth } from '@/features/auth/store'
import { storefrontAuthMessage } from '@/routes/storefront/authMessage'

const schema = z.object({
  email: z.string().min(1, 'Enter your email address').email('That is not a valid email address'),
  password: z.string().min(1, 'Enter your password'),
})

type FormValues = z.infer<typeof schema>

/**
 * Signing in from the shop (M24-03).
 *
 * A separate screen from `/login` rather than a redirect to it, for two reasons that both
 * cost baskets: `/login` sends you to `/dashboard`, which a shopper holding the `guest`
 * role has no permission to see, and it drops you out of the storefront shell, so the
 * basket in the header disappears at the exact moment the shopper is deciding whether to
 * trust the site. Same store, same credentials -- `useAuth.signIn` is the ERP's.
 */
export function StorefrontLogin() {
  const status = useAuth((s) => s.status)
  const signIn = useAuth((s) => s.signIn)
  const navigate = useNavigate()
  const location = useLocation()
  const [formError, setFormError] = useState<string | null>(null)

  const from = (location.state as { from?: string } | null)?.from ?? '/store'

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema) })

  if (status === 'authenticated') return <Navigate to={from} replace />

  const onSubmit = handleSubmit(async ({ email, password }) => {
    setFormError(null)
    try {
      await signIn(email, password)
      navigate(from, { replace: true })
    } catch (error) {
      setFormError(storefrontAuthMessage(error))
    }
  })

  return (
    <div className="mx-auto max-w-md space-y-4 py-6">
      <div className="text-center">
        <h1 className="text-xl font-semibold tracking-tight text-slate-900 dark:text-slate-100">
          Sign in
        </h1>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          Your basket is waiting — it is kept on this device.
        </p>
      </div>

      <Card>
        <CardBody>
          <form onSubmit={onSubmit} noValidate className="space-y-4">
            {formError && <Alert tone="error">{formError}</Alert>}

            <Field label="Email" error={errors.email?.message}>
              {(props) => (
                <Input
                  {...props}
                  {...register('email')}
                  type="email"
                  autoComplete="username"
                  autoFocus
                  placeholder="you@example.com"
                />
              )}
            </Field>

            <Field
              label="Password"
              error={errors.password?.message}
              action={
                <Link
                  to="/forgot-password"
                  className="text-xs font-medium text-brand-600 hover:underline dark:text-brand-400"
                >
                  Forgot password?
                </Link>
              }
            >
              {(props) => (
                <PasswordInput
                  {...props}
                  {...register('password')}
                  autoComplete="current-password"
                  placeholder="••••••••"
                />
              )}
            </Field>

            <Button type="submit" className="w-full" loading={isSubmitting}>
              {isSubmitting ? 'Signing in…' : 'Sign in'}
            </Button>
          </form>
        </CardBody>
      </Card>

      <p className="text-center text-sm text-slate-500 dark:text-slate-400">
        New here?{' '}
        <Link
          to="/store/signup"
          state={location.state}
          className="font-medium text-brand-600 hover:underline dark:text-brand-400"
        >
          Create an account
        </Link>
      </p>
    </div>
  )
}
